import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import { TOOLBAR_CONTROL } from '@/components/ui/pageChrome';
import { TYPE, UI_MUTED } from '@/lib/typography';
import { formatCompactPeriodLabel, resolveReportPeriod } from '@/lib/reporting/periods';
import type { ReportPeriodPreset } from '@/lib/reporting/types';

export function periodControlLabel(
  period: ReportPeriodPreset,
  customFrom: string | null,
  customTo: string | null,
  labelFor: (period: ReportPeriodPreset) => string,
  locale: string,
): string {
  if (period === 'custom' && customFrom && customTo) {
    return formatCompactPeriodLabel(resolveReportPeriod('custom', customFrom, customTo), locale);
  }
  return labelFor(period);
}

interface PeriodFilterProps<T extends ReportPeriodPreset = ReportPeriodPreset> {
  periods: readonly T[];
  value: T;
  customFrom: string | null;
  customTo: string | null;
  onChange: (period: T) => void;
  onCustomChange: (next: { customFrom: string | null; customTo: string | null }) => void;
  labelFor: (period: T) => string;
  locale: string;
  ariaLabel: string;
  fromLabel: string;
  toLabel: string;
}

export function PeriodFilter<T extends ReportPeriodPreset>({
  periods,
  value,
  customFrom,
  customTo,
  onChange,
  onCustomChange,
  labelFor,
  locale,
  ariaLabel,
  fromLabel,
  toLabel,
}: PeriodFilterProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const summary = periodControlLabel(
    value,
    customFrom,
    customTo,
    (period) => labelFor(period as T),
    locale,
  );

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={cn(
          TOOLBAR_CONTROL,
          'inline-flex min-w-[11rem] max-w-[16rem] items-center justify-between gap-2 hover:border-ds-field',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="truncate text-left">{summary}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ds-muted" />
      </button>
      {open && (
        <div
          className="absolute z-30 mt-s1 w-64 rounded-pop border border-ds-line bg-ds-card p-s2 shadow-pop"
          role="listbox"
          aria-label={ariaLabel}
        >
          {periods.map((periodId) => {
            const selected = value === periodId;
            return (
              <button
                key={periodId}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(periodId);
                  if (periodId !== 'custom') setOpen(false);
                }}
                className={cn(
                  `flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left ${TYPE.body} hover:bg-ds-row-hover`,
                  selected
                    ? 'bg-ds-teal-tint text-ds-teal'
                    : 'text-ds-text',
                )}
              >
                <span>{labelFor(periodId)}</span>
                {selected ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
              </button>
            );
          })}
          {value === 'custom' && (
            <div className="mt-1 space-y-2 border-t border-ds-line px-2.5 py-2">
              <label className={`block ${UI_MUTED}`}>
                {fromLabel}
                <input
                  type="date"
                  value={customFrom ?? ''}
                  onChange={(event) =>
                    onCustomChange({
                      customFrom: event.target.value || null,
                      customTo,
                    })
                  }
                  className={cn(TOOLBAR_CONTROL, 'mt-1 w-full font-normal')}
                />
              </label>
              <label className={`block ${UI_MUTED}`}>
                {toLabel}
                <input
                  type="date"
                  value={customTo ?? ''}
                  onChange={(event) =>
                    onCustomChange({
                      customFrom,
                      customTo: event.target.value || null,
                    })
                  }
                  className={cn(TOOLBAR_CONTROL, 'mt-1 w-full font-normal')}
                />
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
